import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  LuArrowDownWideNarrow,
  LuArrowUpDown,
  LuCalendarClock,
  LuDatabase,
  LuFilter,
  LuPlus,
  LuSearch,
  LuUpload,
} from "react-icons/lu";
import { toast } from "react-toastify";

import CategoryFormModal from "../../components/categories/CategoryFormModal";
import CategoryList from "../../components/categories/CategoryList";
import ExportMenu from "../../components/importExport/ExportMenu";
import ImportModal from "../../components/importExport/ImportModal";
import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import FilterSelect, { FilterOption } from "../../components/ui/FilterSelect";
import Input from "../../components/ui/Input";
import { sortLabelsOf } from "../../components/ui/SortableTableHead";
import cx from "../../components/ui/cx";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchCategoriesListData,
  getCategories,
  getEditCategory,
  getTotalCategories,
  setEditCategory,
  toggleCategoryModal,
} from "../../features/categories/categoriesSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { DELETE, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import {
  CategoryDateBy,
  CategorySortBy,
  CategorySortOrder,
  ICategoryFormInput,
} from "../../types/category";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "કેટેગરી" },
];

const listKey = "categories";

const dateByOptions: FilterOption<CategoryDateBy>[] = [
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortByOptions: FilterOption<CategorySortBy>[] = [
  { value: "id", label: "ID" },
  { value: "code", label: "કોડ" },
  { value: "name", label: "કેટેગરી નામ" },
  { value: "created_at", label: "બનાવ્યા તારીખ" },
  { value: "updated_at", label: "સુધાર્યા તારીખ" },
];

const sortLabels = sortLabelsOf(sortByOptions);

const sortOrderOptions: FilterOption<CategorySortOrder>[] = [
  { value: "asc", label: "ચડતો ક્રમ" },
  { value: "desc", label: "ઉતરતો ક્રમ" },
];

const CategoriesPage = () => {
  const dispatch = useAppDispatch();
  const categories = useAppSelector(getCategories);
  const totalCategories = useAppSelector(getTotalCategories);
  const editCategory = useAppSelector(getEditCategory);
  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.categoriesCreate
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.categoriesReport
  );
  // The import updates the categories a file names by a saved name only for a user who may update categories.
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.categoriesUpdate
  );

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    date_by: CategoryDateBy;
    sort_by: CategorySortBy;
    sort_order: CategorySortOrder;
  }

  // The dates filter the date a category was added, newest first, until the user picks otherwise.
  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    date_by: "created_at",
    sort_by: "id",
    sort_order: "desc",
  };

  // The filters in use when the user last left the list, else none.
  const savedFilters: FilterFormInput = savedList.filters || emptyFilters;

  const { handleSubmit, register, reset, getValues, setValue, watch } =
    useForm<FilterFormInput>({ defaultValues: savedFilters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showImport, setShowImport] = useState(false);
  // The order the list on screen was loaded in, which the table headers show. The filter's selects may hold an
  // order that isn't applied yet.
  const [appliedSort, setAppliedSort] = useState<{
    sortBy: CategorySortBy;
    sortOrder: CategorySortOrder;
  }>({ sortBy: savedFilters.sort_by, sortOrder: savedFilters.sort_order });

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowCategoryForm = async () => {
    await dispatch(setEditCategory(null));
    dispatch(toggleCategoryModal(true));
  };

  const handleSaveCategory = async (data: ICategoryFormInput) => {
    let saved = false;

    const payload = {
      name: data.name,
      code: data.code,
      description: data.description,
    };

    try {
      if (editCategory && editCategory.id) {
        await PUT(`/v1/admin/categories/${editCategory.id}`, payload)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              await dispatch(setEditCategory(null));

              if (page === 1) {
                fetchCategoriesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/categories", payload)
          .then(async (response: any) => {
            if (response.data.status === "success") {
              saved = true;

              if (page === 1) {
                fetchCategoriesList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }

    return saved;
  };

  const handleDeleteCategory = async (id: number) => {
    setIsLoading(true);

    try {
      await DELETE(`/v1/admin/categories/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = categories.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchCategoriesList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            setIsLoading(false);

            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          setIsLoading(false);

          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      setIsLoading(false);

      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  // A new filter or import starts again from the first page.
  const showFirstPage = () => {
    if (page === 1) {
      fetchCategoriesList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleOnFilterSubmit = async () => {
    showFirstPage();
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    showFirstPage();
  };

  // Filter values shared by the list and the export.
  const filterParams = () => {
    const data = getValues();

    return {
      search: data?.name || null,
      fromDate: data?.fromDate || null,
      toDate: data?.toDate || null,
      date_by: data.date_by,
      sort_by: data.sort_by,
      sort_order: data.sort_order,
    };
  };

  const fetchCategoriesList = async (page: number, size: number) => {
    const currentFetch = ++fetchId.current;

    setIsLoading(true);

    const params = filterParams();

    dispatch(saveListState({ key: listKey, value: { filters: getValues() } }));
    setAppliedSort({ sortBy: params.sort_by, sortOrder: params.sort_order });

    dispatch(
      fetchCategoriesListData({
        page: page,
        size: size,
        ...params,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  // A table header sorts the list as the filter's ગોઠવો and ક્રમ do, and they show the same choice.
  const handleSort = (
    sortBy: CategorySortBy,
    sortOrder: CategorySortOrder
  ) => {
    setValue("sort_by", sortBy);
    setValue("sort_order", sortOrder);
    showFirstPage();
  };

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchCategoriesList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>કેટેગરી</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div
            className={cx(
              "grid w-full gap-2 sm:ml-auto sm:flex sm:w-auto",
              canCreate ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            <Button
              variant="outline"
              onClick={() => setShowFilter(!showFilter)}
            >
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>

            {canCreate && (
              <Button variant="outline" onClick={() => setShowImport(true)}>
                <LuUpload className="h-4 w-4" />
                ઇમ્પોર્ટ
              </Button>
            )}

            {canCreate && (
              <Button
                variant="primary"
                className="col-span-2"
                onClick={handleShowCategoryForm}
              >
                <LuPlus className="h-4 w-4" />
                નવી કેટેગરી ઉમેરો
              </Button>
            )}
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>શોધો</label>
                    <div className="relative">
                      <LuSearch className={fieldIconClass} />
                      <Input
                        placeholder="નામ અથવા કોડ દ્વારા શોધો"
                        className={fieldInputClass()}
                        {...register("name")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        placeholder="તારીખ દાખલ કરો"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <FilterSelect
                    label="તારીખ ફિલ્ટર"
                    icon={LuCalendarClock}
                    placeholder="તારીખ સિલેક્ટ કરો"
                    options={dateByOptions}
                    value={watch("date_by")}
                    onChange={(value) => value && setValue("date_by", value)}
                  />

                  <FilterSelect
                    label="ગોઠવો"
                    icon={LuArrowUpDown}
                    placeholder="ગોઠવણી સિલેક્ટ કરો"
                    options={sortByOptions}
                    value={watch("sort_by")}
                    onChange={(value) => value && setValue("sort_by", value)}
                  />

                  <FilterSelect
                    label="ક્રમ"
                    icon={LuArrowDownWideNarrow}
                    placeholder="ક્રમ સિલેક્ટ કરો"
                    options={sortOrderOptions}
                    value={watch("sort_order")}
                    onChange={(value) =>
                      value && setValue("sort_order", value)
                    }
                  />
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  {canReport && (
                    <ExportMenu
                      endpoint="/v1/admin/categories/export"
                      getParams={filterParams}
                    />
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <CategoryList
          categories={categories}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
          sortBy={appliedSort.sortBy}
          sortOrder={appliedSort.sortOrder}
          onSort={handleSort}
          sortLabels={sortLabels}
          handleDeleteCategory={handleDeleteCategory}
        />

        {!isLoading && categories.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalCategories}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="કેટેગરી"
          />
        )}
      </div>

      <CategoryFormModal handleSaveCategory={handleSaveCategory} />

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="કેટેગરી ઇમ્પોર્ટ કરો"
        endpoint="/v1/admin/categories"
        help={[
          "કોલમ: નામ, કોડ, ડિસ્ક્રિપ્શન (વૈકલ્પિક).",
          "કોડ માં ફક્ત A-Z, 0-9 અને - ચાલે (- શરૂઆતમાં, છેલ્લે કે સળંગ બે વાર નહીં), વધુમાં વધુ 10 અક્ષર.",
          ...(canUpdate
            ? [
                "પહેલેથી ઉમેરેલા નામ વાળી કેટેગરી ફરીથી નહીં બને, ફાઇલ મુજબ સુધારાશે: એક્સપોર્ટ કરેલી ફાઇલ માં ફેરફાર કરીને ઇમ્પોર્ટ કરો. ખાલી ખાનું ખાલી સેવ થશે.",
                "કેટેગરી તેના નામ થી ઓળખાય છે: ફાઇલ માં બદલેલું નામ નવી કેટેગરી બનાવે છે, તેથી નામ કેટેગરી પેજ પર બદલો.",
              ]
            : ["પહેલેથી ઉમેરેલા નામ વાળી કેટેગરી છોડી દેવામાં આવશે."]),
        ]}
        onImported={showFirstPage}
      />
    </AppLayout>
  );
};
export default CategoriesPage;
